<template>
  <aside
    ref="editor"
    class="journal-column-editor"
    :class="{ 'journal-column-editor--dragging': isDragging }"
    :style="editorPosition"
    role="dialog"
    aria-modal="false"
    aria-label="Додавання стовпчика"
  >
    <header
      class="journal-column-editor__header"
      @pointerdown="startDragging"
    >
      <div>
        <span>Налаштування журналу</span>
        <h3>Додати стовпчик</h3>
      </div>
      <button
        type="button"
        aria-label="Закрити панель додавання стовпчика"
        @click="$emit('cancel')"
      >
        <v-icon size="21">mdi-close</v-icon>
      </button>
    </header>

    <div class="journal-column-editor__body">
      <section>
        <span class="journal-column-editor__step">1. Тип стовпчика</span>
        <div class="journal-column-editor__types">
          <button
            v-for="item in items"
            :key="item.type"
            type="button"
            :class="{
              'journal-column-editor__type--active':
                selectedType.type === item.type,
            }"
            @click="$emit('select-type', item)"
          >
            {{ item.label }}
          </button>
        </div>
      </section>

      <section>
        <span class="journal-column-editor__step">2. Позиція в журналі</span>
        <p>{{ instruction }}</p>
        <div
          class="journal-column-editor__selection"
          :class="{
            'journal-column-editor__selection--ready': hasSelectedRange,
          }"
        >
          <v-icon
            :class="{ 'journal-column-editor__selection-icon--ready': hasSelectedRange }"
            :color="hasSelectedRange ? undefined : '#7f8b9b'"
            size="20"
          >
            {{
              hasSelectedRange
                ? 'mdi-check-circle-outline'
                : 'mdi-cursor-default-click-outline'
            }}
          </v-icon>
          <div>
            <span>Вибрано</span>
            <strong>{{ selectedRangeDescription }}</strong>
          </div>
        </div>
      </section>

      <div class="journal-column-editor__note">
        <v-icon color="#2d68bd" size="19">mdi-information-outline</v-icon>
        Клацніть потрібний заголовок уроку безпосередньо в журналі.
      </div>
    </div>

    <footer class="journal-column-editor__footer">
      <v-btn
        class="journal-column-editor__action journal-column-editor__action--cancel"
        outlined
        color="primary"
        @click="$emit('cancel')"
      >
        Скасувати
      </v-btn>
      <v-btn
        class="journal-column-editor__action journal-column-editor__action--save"
        color="primary"
        :disabled="!hasSelectedRange || operationInProgress"
        @click="$emit('save')"
      >
        Додати стовпчик
      </v-btn>
    </footer>
  </aside>
</template>

<script>
export default {
  name: 'JournalColumnEditor',
  props: {
    items: { type: Array, default: () => [] },
    selectedType: { type: Object, required: true },
    instruction: { type: String, default: '' },
    selectedRangeDescription: { type: String, default: '' },
    hasSelectedRange: Boolean,
    operationInProgress: Boolean,
  },
  data() {
    return {
      dragPosition: null,
      dragOffset: { x: 0, y: 0 },
      isDragging: false,
    };
  },
  computed: {
    editorPosition() {
      if (!this.dragPosition) {
        return null;
      }
      return {
        bottom: 'auto',
        left: `${this.dragPosition.x}px`,
        right: 'auto',
        top: `${this.dragPosition.y}px`,
      };
    },
  },
  mounted() {
    window.addEventListener('resize', this.keepEditorInViewport);
  },
  beforeDestroy() {
    this.stopDragging();
    window.removeEventListener('resize', this.keepEditorInViewport);
  },
  methods: {
    startDragging(event) {
      if (
        event.button !== 0 ||
        event.target.closest('.journal-column-editor__header button')
      ) {
        return;
      }
      const editor = this.$refs.editor;
      if (!editor) {
        return;
      }
      const rect = editor.getBoundingClientRect();
      this.dragPosition = { x: rect.left, y: rect.top };
      this.dragOffset = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      this.isDragging = true;
      window.addEventListener('pointermove', this.dragEditor);
      window.addEventListener('pointerup', this.stopDragging);
      window.addEventListener('pointercancel', this.stopDragging);
      event.preventDefault();
    },
    dragEditor(event) {
      if (!this.isDragging) {
        return;
      }
      this.setEditorPosition(
        event.clientX - this.dragOffset.x,
        event.clientY - this.dragOffset.y
      );
    },
    stopDragging() {
      this.isDragging = false;
      window.removeEventListener('pointermove', this.dragEditor);
      window.removeEventListener('pointerup', this.stopDragging);
      window.removeEventListener('pointercancel', this.stopDragging);
    },
    keepEditorInViewport() {
      if (!this.dragPosition) {
        return;
      }
      this.setEditorPosition(this.dragPosition.x, this.dragPosition.y);
    },
    setEditorPosition(x, y) {
      const editor = this.$refs.editor;
      if (!editor) {
        return;
      }
      const margin = 8;
      const maxX = Math.max(margin, window.innerWidth - editor.offsetWidth - margin);
      const maxY = Math.max(
        margin,
        window.innerHeight - editor.offsetHeight - margin
      );
      this.dragPosition = {
        x: Math.min(Math.max(x, margin), maxX),
        y: Math.min(Math.max(y, margin), maxY),
      };
    },
  },
};
</script>

<style lang="less" scoped>
.journal-column-editor {
  background: #fff;
  border: 1px solid #dce5e1;
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(31, 41, 55, 0.2);
  display: flex;
  flex-direction: column;
  max-height: ~'calc(100vh - 104px)';
  overflow: hidden;
  position: fixed;
  right: 24px;
  top: 84px;
  width: 360px;
  z-index: 20;
}
.journal-column-editor__header {
  align-items: center;
  border-bottom: 1px solid #e5e9ec;
  cursor: grab;
  display: flex;
  justify-content: space-between;
  padding: 16px 18px;
  touch-action: none;
  user-select: none;
}
.journal-column-editor--dragging .journal-column-editor__header {
  cursor: grabbing;
}
.journal-column-editor__header span {
  color: #2d68bd;
  display: block;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.journal-column-editor__header h3 {
  color: #172033;
  font-size: 19px;
  margin: 3px 0 0;
}
.journal-column-editor__header button {
  align-items: center;
  background: #f3f5f6;
  border: 0;
  border-radius: 9px;
  cursor: pointer;
  display: flex;
  height: 36px;
  justify-content: center;
  width: 36px;
}
.journal-column-editor__body {
  overflow-y: auto;
  padding: 17px 18px;
}
.journal-column-editor__body section + section {
  border-top: 1px solid #e8ecee;
  margin-top: 18px;
  padding-top: 18px;
}
.journal-column-editor__step {
  color: #526174;
  display: block;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 9px;
  text-transform: uppercase;
}
.journal-column-editor__types {
  display: grid;
  gap: 7px;
  grid-template-columns: repeat(3, 1fr);
}
.journal-column-editor__types button {
  background: #f7f9fa;
  border: 1px solid #dfe5e8;
  border-radius: 9px;
  color: #4a586b;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  min-height: 40px;
  padding: 7px 8px;
}
.journal-column-editor__types .journal-column-editor__type--active {
  background: var(--theme-banner-background);
  border-color: var(--theme-border-decorative);
  box-shadow: inset 0 0 0 1px var(--theme-border-decorative);
  color: var(--theme-header-background);
}
.journal-column-editor__body p {
  color: #68768a;
  font-size: 13px;
  line-height: 1.45;
  margin: 0 0 11px;
}
.journal-column-editor__selection {
  align-items: center;
  background: #f6f8f9;
  border: 1px dashed #cbd5da;
  border-radius: 10px;
  display: flex;
  gap: 10px;
  min-height: 58px;
  padding: 10px 11px;
}
.journal-column-editor__selection--ready {
  background: var(--theme-banner-background);
  border-color: var(--theme-border-decorative);
  border-style: solid;
}
.journal-column-editor__selection-icon--ready {
  color: var(--theme-header-background) !important;
}
.journal-column-editor__selection span,
.journal-column-editor__selection strong {
  display: block;
}
.journal-column-editor__selection span {
  color: #7a8797;
  font-size: 10px;
  text-transform: uppercase;
}
.journal-column-editor__selection strong {
  color: #263448;
  font-size: 13px;
  line-height: 1.3;
  margin-top: 2px;
}
.journal-column-editor__note {
  align-items: flex-start;
  background: #f1f5fc;
  border-radius: 9px;
  color: #526174;
  display: flex;
  font-size: 12px;
  gap: 8px;
  line-height: 1.4;
  margin-top: 13px;
  padding: 9px 10px;
}
.journal-column-editor__footer {
  border-top: 1px solid #e5e9ec;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding: 13px 18px;
}
.journal-column-editor__footer::v-deep .v-btn {
  border-radius: 9px;
  margin: 0;
}
.journal-column-editor__footer::v-deep .journal-column-editor__action {
  letter-spacing: 0.08em;
}
.journal-column-editor__footer::v-deep .journal-column-editor__action--cancel:not(.v-btn--disabled) {
  border-color: var(--theme-header-background) !important;
  color: var(--theme-header-background) !important;
}
.journal-column-editor__footer::v-deep .journal-column-editor__action--cancel:not(.v-btn--disabled):hover {
  background: var(--theme-banner-background) !important;
}
.journal-column-editor__footer::v-deep .journal-column-editor__action--save:not(.v-btn--disabled) {
  background: var(--theme-header-background) !important;
  border-color: var(--theme-header-background) !important;
  color: var(--theme-header-text) !important;
}
.journal-column-editor__footer::v-deep .journal-column-editor__action--save:not(.v-btn--disabled):hover {
  filter: brightness(0.9);
}
.journal-column-editor__footer::v-deep .journal-column-editor__action--save.v-btn--disabled {
  background: var(--theme-surface-decorative) !important;
  border-color: var(--theme-border-decorative) !important;
  color: var(--theme-accent-decorative) !important;
}
@media (max-width: 700px) {
  .journal-column-editor {
    border-radius: 16px 16px 0 0;
    bottom: 0;
    left: 0;
    max-height: 78vh;
    right: 0;
    top: auto;
    width: 100%;
  }
}
</style>
